import { useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { navigate } from "../router.jsx";
import { money, fmtDate, fmtDateShort, pct, today } from "../format.js";
import {
  Async, AuthImage, Bar, Button, Card, Check, ErrorPanel, Field, Fields, Grid, Input, Modal, Money, Ribbon, Select, Sep, Textarea, TopBar, Wrap, useAsync, useToast,
} from "../ui.jsx";
import DocumentView from "../components/DocumentView.jsx";
import IssuerLogo from "../components/IssuerLogo.jsx";
import { SendModal } from "./InvoiceEditor.jsx";

export default function InvoiceDetail({ id }) {
  const { settings, organization, reloadApp, me } = useApp();
  const canManage = !!me.permissions["invoices.manage"];
  const canPay = !!me.permissions["payments.manage"];
  const toast = useToast();
  const sym = settings.currency_symbol;
  const state = useAsync(() => api.get(`invoices/${id}`), [id]);
  const [modal, setModal] = useState(null);

  const refresh = () => {
    state.reload();
    reloadApp();
  };

  return (
    <Async state={state}>
      {(inv) => {
        const isDraft = inv.status === "draft";
        const isVoid = inv.status === "void";
        const collected = pct(inv.paid, inv.total);

        const timeline = [
          { label: "Created", when: fmtDate(inv.created_at), done: true },
          { label: "Sent", when: inv.sent_at ? fmtDate(inv.sent_at) : "—", done: !!inv.sent_at },
          { label: "Viewed by client", when: inv.viewed_at ? fmtDate(inv.viewed_at) : "—", done: !!inv.viewed_at },
          {
            label: "Paid in full",
            when: inv.display_status === "paid" ? fmtDate(inv.payments[0]?.paid_on) : "—",
            done: inv.display_status === "paid",
          },
        ];

        return (
          <>
            <TopBar crumb="Get paid / Invoices" title={inv.number}>
              <Button onClick={() => api.openBlob(api.invoicePdfPath(inv.id)).catch((e) => toast.push(e.message, "err"))}>⤓ PDF</Button>
              {isDraft ? (
                canManage && (
                  <>
                    <Button onClick={() => navigate(`/invoices/${inv.id}/edit`)}>Edit</Button>
                    <Button kind="pri" onClick={() => setModal("send")}>
                      Send invoice
                    </Button>
                  </>
                )
              ) : (
                <>
                  {!isVoid && (
                    <Button onClick={() => window.open(`/pay/invoice/${inv.public_token}`, "_blank")}>
                      Client view
                    </Button>
                  )}
                  {!isVoid && inv.display_status !== "paid" && (
                    <>
                      {canManage && <Button onClick={() => setModal("remind")}>Send reminder</Button>}
                      {canPay && (
                        <Button kind="pri" onClick={() => setModal("payment")}>
                          Record payment
                        </Button>
                      )}
                    </>
                  )}
                </>
              )}
            </TopBar>

            <Wrap>
              {isVoid && <Ribbon tone="warn">This invoice was voided on {fmtDate(inv.voided_at)}.</Ribbon>}
              {inv.display_status === "overdue" && (
                <Ribbon tone="warn">
                  ⚠ {inv.days_late} days overdue — {money(inv.balance, sym)} outstanding.
                </Ribbon>
              )}

              <Grid cols="1fr 322px" style={{ alignItems: "start" }}>
                <DocumentView
                  doc={{ ...inv, status: inv.display_status }}
                  kind="invoice"
                  company={inv.issuer || organization || settings}
                  logo={
                    <IssuerLogo issuer={inv.issuer || organization || settings} live={organization || settings} />
                  }
                  client={{
                    name: inv.client_name,
                    contact_name: inv.contact_name,
                    address: inv.client_address,
                    email: inv.client_email,
                  }}
                />

                <div style={{ display: "grid", gap: 14 }}>
                  <Card title="Summary">
                    <div style={{ display: "grid", gap: 10 }}>
                      <div className="row">
                        <span className="muted sm">Total</span>
                        <div className="grow" />
                        <Money v={inv.total} sym={sym} bold />
                      </div>
                      <div className="row">
                        <span className="muted sm">Paid</span>
                        <div className="grow" />
                        <span className="num" style={{ color: "var(--ok)" }}>
                          {money(inv.paid, sym)}
                        </span>
                      </div>
                      <Sep />
                      <div className="row">
                        <b>Balance due</b>
                        <div className="grow" />
                        <b className="num" style={{ fontSize: 17 }}>
                          {money(inv.balance, sym)}
                        </b>
                      </div>
                      <Bar pct={collected} />
                      <div className="tiny muted">
                        {collected}% collected ·{" "}
                        {inv.display_status === "paid"
                          ? "Settled"
                          : inv.days_late > 0
                          ? `${inv.days_late} days overdue`
                          : `Due ${fmtDateShort(inv.due_date)}`}
                      </div>
                    </div>
                  </Card>

                  <Card title="Timeline">
                    <div style={{ display: "grid", gap: 12 }}>
                      {timeline.map((t) => (
                        <div className="row" key={t.label} style={{ alignItems: "flex-start", gap: 9 }}>
                          <span
                            style={{
                              width: 15,
                              height: 15,
                              borderRadius: "50%",
                              flex: "none",
                              marginTop: 2,
                              display: "grid",
                              placeItems: "center",
                              fontSize: 9,
                              color: "var(--cream)",
                              background: t.done ? "var(--brand)" : "var(--line)",
                            }}
                          >
                            {t.done ? "✓" : ""}
                          </span>
                          <div>
                            <div className="sm" style={t.done ? { fontWeight: 600 } : { color: "var(--ink-3)" }}>
                              {t.label}
                            </div>
                            <div className="tiny muted">{t.when}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>

                  <Card
                    title="Payments"
                    bare
                    right={
                      canPay && !isDraft && !isVoid && inv.display_status !== "paid" ? (
                        <Button sm kind="gho" onClick={() => setModal("payment")}>
                          ＋ Add
                        </Button>
                      ) : null
                    }
                  >
                    {inv.payments.length ? (
                      inv.payments.map((p) => (
                        <div key={p.id} style={{ padding: "11px 16px", borderBottom: "1px solid var(--line-2)" }}>
                          <div className="row">
                            <Money v={p.amount} sym={sym} bold />
                            <div className="grow" />
                            <span className="tiny muted">{fmtDateShort(p.paid_on)}</span>
                          </div>
                          <div className="tiny muted">
                            {p.method}
                            {p.reference ? ` · ${p.reference}` : ""}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="empty sm" style={{ padding: 24 }}>
                        No payments recorded
                      </div>
                    )}
                  </Card>

                  {isDraft && canManage && (
                    <Card title="Danger zone">
                      <Button
                        kind="dgr"
                        sm
                        onClick={async () => {
                          try {
                            await api.del(`invoices/${inv.id}`);
                            toast.push("Draft deleted");
                            reloadApp();
                            navigate("/invoices");
                          } catch (e) {
                            toast.push(e.message, "err");
                          }
                        }}
                      >
                        Delete this draft
                      </Button>
                    </Card>
                  )}

                  {!isDraft && !isVoid && canManage && (
                    <Card title="Correction">
                      <p className="tiny muted" style={{ marginTop: 0 }}>
                        Sent invoices can't be edited — that keeps the numbering and audit trail intact. Void this
                        one and issue a replacement instead.
                      </p>
                      <Button kind="dgr" sm onClick={() => setModal("void")}>
                        Void invoice
                      </Button>
                    </Card>
                  )}
                </div>
              </Grid>
            </Wrap>

            {modal === "send" && (
              <SendModal invoiceId={inv.id} onClose={() => setModal(null)} onSent={refresh} />
            )}
            {modal === "payment" && (
              <PaymentModal invoice={inv} sym={sym} onClose={() => setModal(null)} onSaved={refresh} />
            )}
            {modal === "remind" && (
              <ReminderModal invoice={inv} sym={sym} onClose={() => setModal(null)} />
            )}
            {modal === "void" && (
              <VoidModal invoice={inv} onClose={() => setModal(null)} onDone={refresh} />
            )}
          </>
        );
      }}
    </Async>
  );
}

/* -------------------------------------------------------------- modals */

function PaymentModal({ invoice, sym, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({
    amount: Number(invoice.balance).toFixed(2),
    paid_on: today(),
    method: "EFT",
    reference: "",
    email_receipt: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`invoices/${invoice.id}/payments`, { ...form, amount: Number(form.amount) });
      toast.push("Payment recorded");
      onSaved();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Record a payment"
      width={480}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={submit}>
            Record payment
          </Button>
        </>
      }
    >
      <Fields>
        {error && <ErrorPanel error={error} />}
        <Ribbon>
          {invoice.number} · {money(invoice.balance, sym)} outstanding
        </Ribbon>
        <Fields cols={2}>
          <Field label="Amount">
            <Input value={form.amount} onChange={(e) => set("amount", e.target.value)} />
          </Field>
          <Field label="Date received">
            <Input type="date" value={form.paid_on} onChange={(e) => set("paid_on", e.target.value)} />
          </Field>
        </Fields>
        <Fields cols={2}>
          <Field label="Method">
            <Select value={form.method} onChange={(e) => set("method", e.target.value)}>
              {["EFT", "Cheque", "Credit card", "Cash", "Other"].map((m) => (
                <option key={m}>{m}</option>
              ))}
            </Select>
          </Field>
          <Field label="Reference">
            <Input
              placeholder="Cheque no. / transaction id"
              value={form.reference}
              onChange={(e) => set("reference", e.target.value)}
            />
          </Field>
        </Fields>
        <Check
          label="Email the client a receipt"
          checked={form.email_receipt}
          onChange={(v) => set("email_receipt", v)}
        />
      </Fields>
    </Modal>
  );
}

function ReminderModal({ invoice, sym, onClose }) {
  const toast = useToast();
  const [tone, setTone] = useState("firm");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(`invoices/${invoice.id}/remind`, { tone, message, to: invoice.client_email });
      toast.push(res.mail?.skipped ? "Email isn't configured yet — nothing was sent" : "Reminder sent", res.mail?.skipped ? "err" : "ok");
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Send a reminder"
      width={480}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={submit}>
            Send reminder
          </Button>
        </>
      }
    >
      <Fields>
        {error && <ErrorPanel error={error} />}
        <Ribbon tone={invoice.days_late > 0 ? "warn" : undefined}>
          {invoice.days_late > 0
            ? `${invoice.days_late} days overdue with ${money(invoice.balance, sym)} outstanding.`
            : `Due ${fmtDate(invoice.due_date)} — ${money(invoice.balance, sym)} outstanding.`}
        </Ribbon>
        <Field label="Tone">
          <Select value={tone} onChange={(e) => setTone(e.target.value)}>
            <option value="friendly">Friendly nudge</option>
            <option value="firm">Firm reminder</option>
            <option value="final">Final notice</option>
          </Select>
        </Field>
        <Field label="Replace the default message (optional)">
          <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
        </Field>
      </Fields>
    </Modal>
  );
}

function VoidModal({ invoice, onClose, onDone }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`invoices/${invoice.id}/void`, {});
      toast.push(`${invoice.number} voided`);
      onDone();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Void ${invoice.number}?`}
      width={440}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={submit}>
            Void invoice
          </Button>
        </>
      }
    >
      {error && <ErrorPanel error={error} />}
      <p className="sm dim" style={{ marginTop: 0 }}>
        The invoice stays in your records marked void, so the numbering stays intact. Any time entries and
        expenses billed on it are released and can be invoiced again.
      </p>
    </Modal>
  );
}
